// services/adminService.ts

import api from "./api";

export interface UserFilters {
  status?: "active" | "inactive" | "kyc_pending";
  role?: "user" | "admin" | "all";
  search?: string;
  limit?: number;
  page?: number;
}

/**
 * 3-tier activity-based withdrawal settings.
 *
 * Tier resolution (highest first):
 *   High : 7-day earnings >= tier1_threshold  →  tier1_pct% of earnings
 *   Mid  : 7-day earnings >= tier2_threshold  →  tier2_pct% of earnings
 *   Low  : below tier2_threshold              →  tier3_pct% of earnings
 *
 * floor_limit prevents anyone's limit from dropping below a minimum XRP amount.
 * allowed_days restricts which days of the week withdrawals can be submitted.
 */
export interface WithdrawalSettings {
  allowed_days: number[]; // 0 = Sunday … 6 = Saturday
  tier1_threshold: number; // e.g. 500  — 7-day earnings to qualify as High
  tier1_pct: number; // e.g. 80   — % of 7-day earnings for High tier
  tier2_threshold: number; // e.g. 100  — 7-day earnings to qualify as Mid
  tier2_pct: number; // e.g. 50   — % of 7-day earnings for Mid tier
  tier3_pct: number; // e.g. 20   — % of 7-day earnings for Low tier
  floor_limit: number; // e.g. 20   — absolute minimum limit (XRP)
  level_bonus_pct: number; // e.g. 40   — % of all-time level-up bonuses added on top
}

export interface BroadcastMessage {
  message: string;
  is_active: boolean;
  type: "info" | "warning" | "success" | "danger";
  speed: number;
}

const adminService = {
  getDashboardStats: async () => {
    const response = await api.get("/admin/dashboard/stats");
    return response.data;
  },

  getUsers: async (filters?: UserFilters) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.role && filters.role !== "all")
      params.append("role", filters.role);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.page) params.append("page", filters.page.toString());
    const response = await api.get(`/admin/users?${params.toString()}`);
    return response.data;
  },

  getAllKycDocuments: async (filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const response = await api.get(`/admin/kyc/all?${params.toString()}`);
    return response.data;
  },

  toggleUserStatus: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/toggle-status`);
    return response.data;
  },

  updateUserRole: async (userId: number, role: string) => {
    const response = await api.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  },

  getPendingKyc: async () => {
    const response = await api.get("/admin/kyc/pending");
    return response.data;
  },

  approveKyc: async (kycId: number, notes?: string) => {
    const response = await api.post(`/admin/kyc/${kycId}/approve`, { notes });
    return response.data;
  },

  rejectKyc: async (kycId: number, rejectionReason: string) => {
    const response = await api.post(`/admin/kyc/${kycId}/reject`, {
      rejection_reason: rejectionReason,
    });
    return response.data;
  },

  getPendingDeposits: async () => {
    const response = await api.get("/admin/deposits/pending");
    return response.data;
  },

  confirmDeposit: async (
    transactionId: number,
    txHash: string,
    adminNotes?: string,
    isAdminCredit?: boolean,
  ) => {
    const response = await api.post(
      `/admin/deposits/${transactionId}/confirm`,
      {
        tx_hash: txHash,
        admin_notes: adminNotes,
        is_admin_credit: isAdminCredit ?? false,
      },
    );
    return response.data;
  },

  rejectDeposit: async (transactionId: number, rejectionReason: string) => {
    const response = await api.post(`/admin/deposits/${transactionId}/reject`, {
      rejection_reason: rejectionReason,
    });
    return response.data;
  },

  getPendingWithdrawals: async () => {
    const response = await api.get("/admin/withdrawals/pending");
    return response.data;
  },

  processWithdrawal: async (
    transactionId: number,
    txHash: string,
    adminNotes?: string,
  ) => {
    const response = await api.post(
      `/admin/withdrawals/${transactionId}/process`,
      { tx_hash: txHash, admin_notes: adminNotes },
    );
    return response.data;
  },

  rejectWithdrawal: async (transactionId: number, rejectionReason: string) => {
    const response = await api.post(
      `/admin/withdrawals/${transactionId}/reject`,
      { rejection_reason: rejectionReason },
    );
    return response.data;
  },

  // ── Withdrawal Settings ────────────────────────────────────────────────────

  getWithdrawalSettings: async (): Promise<WithdrawalSettings> => {
    const response = await api.get("/admin/withdrawal-settings");
    return response.data;
  },

  updateWithdrawalSettings: async (
    settings: WithdrawalSettings,
  ): Promise<WithdrawalSettings & { message: string }> => {
    const response = await api.put("/admin/withdrawal-settings", settings);
    return response.data;
  },

  // ── XRP Addresses ──────────────────────────────────────────────────────────

  getXrpAddresses: async () => {
    const response = await api.get("/admin/xrp-addresses");
    return response.data;
  },

  createXrpAddress: async (addressData: any) => {
    const response = await api.post("/admin/xrp-addresses", addressData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  updateXrpAddress: async (id: number, addressData: any) => {
    const response = await api.put(`/admin/xrp-addresses/${id}`, addressData);
    return response.data;
  },

  deleteXrpAddress: async (id: number) => {
    const response = await api.delete(`/admin/xrp-addresses/${id}`);
    return response.data;
  },

  uploadQrCode: async (id: number, qrCode: File) => {
    const formData = new FormData();
    formData.append("qr_code", qrCode);
    const response = await api.post(
      `/admin/xrp-addresses/${id}/upload-qr`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  // ── Airdrops ───────────────────────────────────────────────────────────────

  getAirdrops: async () => {
    const response = await api.get("/admin/airdrops");
    return response.data;
  },

  createAirdrop: async (airdropData: any) => {
    const response = await api.post("/admin/airdrops", airdropData);
    return response.data;
  },

  updateAirdrop: async (id: number, data: any) => {
    const response = await api.put(`/admin/airdrops/${id}`, data);
    return response.data;
  },

  deleteAirdrop: async (id: number) => {
    const response = await api.delete(`/admin/airdrops/${id}`);
    return response.data;
  },

  toggleAirdropStatus: async (id: number) => {
    const response = await api.post(`/admin/airdrops/${id}/toggle-status`);
    return response.data;
  },

  distributeAirdrop: async (id: number) => {
    const response = await api.post(`/admin/airdrops/${id}/distribute-now`);
    return response.data;
  },

  // ── System / Trading ───────────────────────────────────────────────────────

  recalculateEarnings: async () => {
    const response = await api.post("/admin/recalculate-earnings");
    return response.data;
  },

  checkSystemHealth: async () => {
    const response = await api.get("/admin/system-health");
    return response.data;
  },

  getAllDeposits: async (filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const response = await api.get(
      `/admin/transactions/deposits?${params.toString()}`,
    );
    return response.data;
  },

  getAllWithdrawals: async (filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const response = await api.get(
      `/admin/transactions/withdrawals?${params.toString()}`,
    );
    return response.data;
  },

  getTradingSettings: async () => {
    const response = await api.get("/admin/trading/settings");
    return response.data;
  },

  updateTradingSettings: async (settingsData: any) => {
    const response = await api.put("/admin/trading/settings", settingsData);
    return response.data;
  },

  toggleTradingStatus: async () => {
    const response = await api.post("/admin/trading/toggle-status");
    return response.data;
  },

  manualDistribution: async () => {
    const response = await api.post("/admin/trading/manual-distribution");
    return response.data;
  },

  getTradingStatistics: async () => {
    const response = await api.get("/admin/trading/statistics");
    return response.data;
  },

  // ── Broadcast Message ──────────────────────────────────────────────────────

  getBroadcastMessage: async (): Promise<BroadcastMessage> => {
    const response = await api.get("/admin/broadcast");
    return response.data;
  },

  updateBroadcastMessage: async (
    data: BroadcastMessage,
  ): Promise<{ message: string; broadcast: BroadcastMessage }> => {
    const response = await api.put("/admin/broadcast", data);
    return response.data;
  },
};

export default adminService;
